(ns dre.components.force-directed-graph
  (:require
   [applied-science.js-interop :as j]
   ["d3" :as d3]
   ["d3-force-boundary" :as d3-force-boundary]
   [dre.utils.bem :as bem]
   [reagent.core :as r]
   [goog.dom :as gdom]
   ["react-sizeme" :as react-sizeme]))

(def component-name :force-directed-graph)

;; Public API

(defn sqrt [x] (Math/sqrt x))

(defn force-graph
  [element data node-click-fn selected-nodes w h]
  (let [k (sqrt (/ (count (:nodes data)) (* w h)))
        graph (clj->js data)
        d3-el (j/call d3 :select element)
        svg (.. (j/call d3-el :append "svg")
                (attr "fill" "#d046ff"))
        links (.-links graph)
        nodes (.-nodes graph)
        link (.. svg
                 (append "g")
                 (attr "stroke" "#999")
                 (selectAll)
                 (data links)
                 (join "line")
                 (attr "stroke-width" 1))
        node (.. svg
                 (append "g")
                 (attr "stroke" "#999")
                 (attr "stroke-width" 1)
                 (selectAll)
                 (data nodes)
                 (join "circle")
                 (attr "class" "author-node")
                 (attr "r" #(j/get % :size))
                 (attr "fill" (fn [d] (if (contains? selected-nodes (j/get d :index))
                                        "#d046ff"
                                        "#d0d0d0")))
                 (on "click" node-click-fn))
        node (.. node
                 (on "mouseout"
                     (fn [e d]
                       (.. (j/call d3 :select (str "#force-directed-graph-tooltip"))
                           (style "opacity" 0))
                       (.. (j/call link :classed "active" false)
                           (order))
                       (.. (j/call node :classed "active" false)
                           (order))))
                 (on "mouseover"
                     (fn [e d]
                       (let [dy -10
                             dx 10
                             related-nodes (set (flatten (mapv (fn [l]
                                                                 (if (or (= (j/get-in l [:source :index]) (j/get-in d [:index]))
                                                                         (= (j/get-in l [:target :index]) (j/get-in d [:index])))
                                                                   [(js->clj (j/get-in l [:source :index])) (js->clj (j/get-in l [:target :index]))]
                                                                   [])) links)))]
                         (.. (j/call d3 :select (str "#force-directed-graph-tooltip"))
                             (style "top" (str (+ dy (.-y d)) "px"))
                             (style "left" (str (+ dx (.-x d)) "px"))
                             (style "opacity" 1.0)
                             (select ".tooltip-inner")
                             (text #(j/get d :name)))
                         (.. (j/call link :classed "active" (fn [l] (or (= (j/get l :source) d)
                                                                        (= (j/get l :target) d))))
                             (order))
                         (.. (j/call node :classed "active" (fn [l] (contains? related-nodes (j/get l :index))))
                             (order))))))
        ticked (fn []
                 (.. link
                     (attr "x1" (fn [d] (j/get (j/get d :source) :x)))
                     (attr "y1" (fn [d] (j/get (j/get d :source) :y)))
                     (attr "x2" (fn [d] (j/get (j/get d :target) :x)))
                     (attr "y2" (fn [d] (j/get (j/get d :target) :y))))
                 (.. node
                     (attr "cx" #(j/get % :x))
                     (attr "cy" #(j/get % :y))))
        simulation (.. (j/call d3 :forceSimulation nodes)
                       (force "link" (.. (j/call d3 :forceLink links)
                                         (id #(j/get % :index))
                                         (distance 30)))
                       (force "charge" (.. (j/call d3 :forceManyBody)
                                           (strength (/ -0.5 k))))
                       #_(force "center" (j/call d3 :forceCenter (/ w 2) (/ h 2)))
                       (force "boundary" (d3-force-boundary 10 10 (- w 10) (- h 10)))
                       (force "collide" (.. (j/call d3 :forceCollide)
                                              (radius 15)))
                       #_(tick 10)
                       (on "tick" ticked))]

    (.. svg
        (attr "width" w)
        (attr "height" h)
        (attr "style" "max-width: 100%; height: auto;")
        (attr "viewBox" (into-array [0 0 w h])))))

(defn force-directed-graph*
  [graph node-click-fn selected-nodes width height]
  (let [element (atom nil)
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint!
        (fn [this data node-click-fn selected-nodes width height]
          (destroy! this)
          (force-graph @element data node-click-fn selected-nodes width height))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this graph node-click-fn selected-nodes width height))
      :component-will-unmount
      (fn [this]
        (destroy! this))
      :component-did-update
      (fn [this [_ graph _ selected-nodes width height]] 
        (let [[new-graph new-node-click-fn new-selected-nodes new-width new-height] (rest (r/argv this))
              update? (or 
                       (not= new-graph graph)
                       (not= new-selected-nodes selected-nodes)
                       (not= new-width width)
                       (not= new-height height))]
          (when update?
            (repaint! this new-graph new-node-click-fn new-selected-nodes new-width new-height))))
      :reagent-render
      (fn [graph node-click-fn selected-nodes width height]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :style
           {:width "100%"
            :height "100%"}}
          (str graph)]
         [:div.tooltip.bs
          {:id :force-directed-graph-tooltip}
          [:div.tooltip-inner]]])})))

(defn force-directed-graph
  [graph node-click-fn selected-nodes]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [force-directed-graph*
                       graph
                       node-click-fn
                       selected-nodes
                       (:width (:size props))
                       (:height (:size props))])))]])
